import { envConfigs } from '@config/envConfigs';
import { IPagedResultsV1 } from '@gcl/react-core';
import { useQuery } from '@tanstack/react-query';
import { AxiosErrorResponse } from '@core/error.models';
import { IPaginatedApiBasePropsWithSortByQuery, ISearchApiQueryParamsProps } from './api.models';
import { isValidPaginatedApiBaseProps } from './api.services';
import { apiServerGet, apiServerPost } from './axios.services';

export const createSearchWithCustomPropsAsyncFn = <FilterType, ResponseType>(apiRoute: string) => {
  return async (params: ISearchApiQueryParamsProps, body: FilterType): Promise<ResponseType> => {
    const result = await apiServerPost<ResponseType>(`${envConfigs.apiBaseUrl}${apiRoute}`, body, { params });
    return result.data;
  };
};
export interface ISearchPropsBase<FilterType> extends IPaginatedApiBasePropsWithSortByQuery {
  filter: FilterType | undefined;
}

export const createSearchAsyncFn = <FilterType, ItemType>(apiRoute: string, isGet = false) => {
  // TODO default to use get method once all search updated
  if (isGet)
    return async ({ filter, ...apiBaseProps }: ISearchPropsBase<FilterType>): Promise<IPagedResultsV1<ItemType>> => {
      const result = await apiServerGet<IPagedResultsV1<ItemType>>(`${envConfigs.apiBaseUrl}${apiRoute}`, {
        params: { ...(filter || {}), ...apiBaseProps },
      });
      return result.data;
    };
  return async ({ filter, ...apiBaseProps }: ISearchPropsBase<FilterType>): Promise<IPagedResultsV1<ItemType>> => {
    const result = await apiServerPost<IPagedResultsV1<ItemType>>(
      `${envConfigs.apiBaseUrl}${apiRoute}`,
      {},
      {
        params: { ...(filter || {}), ...apiBaseProps },
      },
    );
    return result.data;
  };
};

export interface ICreateUseFetchSearchWithCustomPropsFnProps<FilterType, ResponseType> {
  queryName: string;
  searchAsyncFn: (queryParams: ISearchApiQueryParamsProps, body: FilterType) => Promise<ResponseType>;
  enabled?: boolean;
}

export const createUseFetchSearchWithCustomPropsFn = <FilterType, ResponseType>({
  queryName,
  searchAsyncFn,
  enabled,
}: ICreateUseFetchSearchWithCustomPropsFnProps<FilterType, ResponseType>) => {
  return (queryParams: ISearchApiQueryParamsProps, body: FilterType) =>
    useQuery<ResponseType, AxiosErrorResponse>(
      [queryName, queryParams, body],
      async () => {
        const result = await searchAsyncFn(queryParams, body);
        return result;
      },
      {
        enabled,
        keepPreviousData: true,
      },
    );
};

export interface ICreateUseFetchSearchFnProps<FilterType, ItemType> {
  queryName: string;
  searchAsyncFn: (searchProps: ISearchPropsBase<FilterType>) => Promise<IPagedResultsV1<ItemType>>;
}

export const createUseFetchSearchFn = <FilterType, ItemType>({
  queryName,
  searchAsyncFn,
}: ICreateUseFetchSearchFnProps<FilterType, ItemType>) => {
  return (props: ISearchPropsBase<FilterType>, enabled = true) =>
    useQuery<IPagedResultsV1<ItemType>, AxiosErrorResponse>(
      [props.projectId, queryName, props.filter, props.sortByField, props.isDesc, props.pageSize, props.pageNumber],
      async () => {
        const result = await searchAsyncFn(props);
        return result;
      },
      {
        enabled: isValidPaginatedApiBaseProps(props) && enabled,
        keepPreviousData: true,
      },
    );
};
